import { endOfMonth } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
  CalendarCheck2,
  CircleX,
  ChartColumn,
  ClipboardList,
  UsersRound,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { BackButton } from "../../../components/button/back-button";
import { DashboardKpiCard } from "../../../components/dashboard-kpi-card";
import { MetricHelp } from "../../../components/metric-help";
import { useToast } from "../../../contexts/toast/toast-context";
import { useTenantFeatures } from "../../../contexts/tenant-features";
import { useAuth } from "../../../hooks/use-auth";
import {
  APP_RESOURCES,
  AccessControl,
} from "../../../permissions/access-control";
import api from "../../../services/api";
import { THEME } from "../../../styles/theme";
import { getApiErrorMessage, months } from "../../../utils";
import {
  clampMonth,
  clampYear,
  getBrowserTimezone,
  resolvePresetRange,
  toReportPeriodQuery,
  type ReportGranularity,
  type ReportPreset,
} from "../../../utils/report-period";

type WindowUsedDto = {
  start: string;
  end: string;
  tz: string;
  granularity: ReportGranularity;
};

type VolumeByPeriodData = {
  period: string;
  label: string;
  year: number;
  month: number;
  conversations: number;
  scheduled: number;
  canceled: number;
  confirmed: number;
};

type VolumeByPeriodResponse = {
  data: VolumeByPeriodData[];
  windowUsed: WindowUsedDto;
};

type VolumeKpis = {
  totalConversations: number;
  scheduled: number;
  unscheduled: number;
  conversionRate: number;
};

type StatusKpis = {
  totalClosed: number;
  totalInAttendance: number;
};

type StatusReportData = {
  closedByAI: number;
  closedByHumanAttendance: number;
  closedByTimeout: number;
  aiAttendance: number;
  waitingHumanAttendance: number;
  humanAttendance: number;
  windowUsed?: WindowUsedDto;
  operationWidgets?: OperationWidgetsData;
  openProtocols?: number;
};

type OperationWidgetsData = {
  heatmap: Array<{
    weekday: number;
    weekdayLabel: string;
    hour: number;
    total: number;
  }>;
  productivityRanking: Array<{
    attendantId: string;
    attendantName: string;
    resolved: number;
    frtMedianMinutes: number | null;
    mttrMedianMinutes: number | null;
    slaBreached: number;
    slaTotal: number;
    slaBreachRate: number;
  }>;
};

type PeriodSelectionPreset = ReportPreset | "custom";

const PRESET_OPTIONS: Array<{ label: string; value: PeriodSelectionPreset }> = [
  { label: "Hoje", value: "today" },
  { label: "Últimos 7 dias", value: "last7Days" },
  { label: "Últimos 30 dias", value: "last30Days" },
  { label: "Mês atual", value: "currentMonth" },
  { label: "Personalizado", value: "custom" },
];

const WEEKDAY_ORDER = [
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sab" },
  { value: 7, label: "Dom" },
];

const HOURS = Array.from({ length: 24 }, (_, index) => index);

function ChartSkeleton({ height = 250 }: { height?: number }) {
  return (
    <div className="app-card animate-pulse p-4">
      <div className="h-3 w-32 rounded bg-slate-200" />
      <div
        className="mt-4 w-full rounded bg-slate-100"
        style={{ height: `${height}px` }}
      />
    </div>
  );
}

function MetricCardSkeleton() {
  return (
    <div className="app-card animate-pulse p-4">
      <div className="h-3 w-28 rounded bg-slate-200" />
      <div className="mt-3 h-8 w-24 rounded bg-slate-100" />
    </div>
  );
}

function InlineError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <div>{message}</div>
      <button type="button" className="btn-link-danger mt-2" onClick={onRetry}>
        Tentar novamente
      </button>
    </div>
  );
}

function formatMinutes(value: number | null | undefined) {
  if (value === null || value === undefined) return "--";
  return `${value.toFixed(1)} min`;
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) return "--";
  return `${value.toFixed(2)}%`;
}

function formatHour(hour: number) {
  return `${String(hour).padStart(2, "0")}:00`;
}

function heatCellClass(total: number, maxTotal: number) {
  if (total <= 0 || maxTotal <= 0) return "bg-slate-100 text-slate-500";

  const ratio = total / maxTotal;
  if (ratio > 0.75) return "bg-blue-600 text-white";
  if (ratio > 0.5) return "bg-blue-500 text-white";
  if (ratio > 0.25) return "bg-blue-300 text-blue-900";
  return "bg-blue-100 text-blue-700";
}

export default function Dashboard() {
  const { user } = useAuth();
  const { protocolsEnabled, schedulingEnabled } = useTenantFeatures();
  const { error } = useToast();
  const canViewOperationWidgets = AccessControl.canView(
    APP_RESOURCES.PANEL_DASHBOARD_OPERATION_WIDGETS,
    user?.role,
  );
  const currentDate = new Date();
  const initialRange = resolvePresetRange("currentMonth", currentDate);

  const [volumeKpis, setVolumeKpis] = useState<VolumeKpis>({
    totalConversations: 0,
    scheduled: 0,
    unscheduled: 0,
    conversionRate: 0,
  });

  const [statusData, setStatusData] = useState<
    { name: string; value: number }[]
  >([]);
  const [attendanceData, setAttendanceData] = useState<
    { name: string; value: number }[]
  >([]);
  const [statusKpis, setStatusKpis] = useState<StatusKpis>({
    totalClosed: 0,
    totalInAttendance: 0,
  });
  const [openProtocols, setOpenProtocols] = useState(0);
  const [operationWidgets, setOperationWidgets] =
    useState<OperationWidgetsData | null>(null);

  const [preset, setPreset] = useState<PeriodSelectionPreset>("currentMonth");
  const [startDate, setStartDate] = useState<Date | null>(
    initialRange.startDate,
  );
  const [endDate, setEndDate] = useState<Date | null>(initialRange.endDate);
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const [granularity, setGranularity] = useState<ReportGranularity>(
    initialRange.granularity,
  );
  const [refreshToken, setRefreshToken] = useState(0);
  const [isLoadingVolume, setIsLoadingVolume] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [volumeError, setVolumeError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);

  const timezoneOptions = useMemo(() => {
    const options = [timezone, "America/Sao_Paulo", "America/Fortaleza", "UTC"];
    return Array.from(new Set(options));
  }, [timezone]);
  const integerFormatter = useMemo(() => new Intl.NumberFormat("pt-BR"), []);
  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    [],
  );
  const combinedVolumeKpis = useMemo(() => {
    const totalConversations =
      statusKpis.totalClosed + statusKpis.totalInAttendance;
    const conversationsWithAppointment = volumeKpis.scheduled;
    const conversationsWithoutAppointment = Math.max(
      totalConversations - conversationsWithAppointment,
      0,
    );
    const conversionRate =
      totalConversations > 0
        ? (conversationsWithAppointment / totalConversations) * 100
        : 0;

    return {
      totalConversations,
      scheduled: conversationsWithAppointment,
      unscheduled: conversationsWithoutAppointment,
      conversionRate,
    };
  }, [
    volumeKpis.scheduled,
    statusKpis.totalClosed,
    statusKpis.totalInAttendance,
  ]);
  const maxHeat = useMemo(() => {
    const values = operationWidgets?.heatmap.map((item) => item.total) ?? [];
    if (!values.length) return 0;
    return Math.max(...values);
  }, [operationWidgets]);
  const heatmapByHour = useMemo(() => {
    const buckets = new Map<number, Record<number, number>>();

    for (const hour of HOURS) {
      buckets.set(hour, {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        6: 0,
        7: 0,
      });
    }

    for (const item of operationWidgets?.heatmap ?? []) {
      const bucket = buckets.get(item.hour);
      if (!bucket) continue;
      bucket[item.weekday] = item.total;
    }

    return buckets;
  }, [operationWidgets]);

  useEffect(() => {
    const fetchVolumeByPeriod = async () => {
      setIsLoadingVolume(true);
      setVolumeError(null);

      try {
        const response = await api.get<VolumeByPeriodResponse>(
          "/report/volume-by-period",
          {
            params: toReportPeriodQuery({
              startDate,
              endDate,
              timezone,
              granularity: "month",
            }),
          },
        );

        const totals = response.data.data.reduce(
          (accumulator, item) => ({
            conversations: accumulator.conversations + item.conversations,
            scheduled: accumulator.scheduled + item.scheduled,
          }),
          { conversations: 0, scheduled: 0 },
        );
        const conversionRate =
          totals.conversations > 0
            ? (totals.scheduled / totals.conversations) * 100
            : 0;

        setVolumeKpis({
          totalConversations: totals.conversations,
          scheduled: totals.scheduled,
          unscheduled: Math.max(totals.conversations - totals.scheduled, 0),
          conversionRate,
        });
      } catch (err) {
        const message = getApiErrorMessage(err);
        setVolumeError(message);
        error(message);
      } finally {
        setIsLoadingVolume(false);
      }
    };

    fetchVolumeByPeriod();
  }, [startDate, endDate, timezone, error, refreshToken]);

  useEffect(() => {
    const fetchStatusData = async () => {
      setIsLoadingStatus(true);
      setStatusError(null);

      try {
        const response = await api.get<StatusReportData>(
          "/report/conversations-by-status",
          {
            params: {
              ...toReportPeriodQuery({
                startDate,
                endDate,
                timezone,
                granularity,
              }),
              includeOperationWidgets: canViewOperationWidgets || undefined,
            },
          },
        );

        const closedFormatted = [
          { name: "IA", value: response.data.closedByAI },
          {
            name: "Atendimento Humano",
            value: response.data.closedByHumanAttendance,
          },
          {
            name: "Timeout",
            value: response.data.closedByTimeout,
          },
        ];

        const attendanceFormatted = [
          { name: "IA", value: response.data.aiAttendance },
          {
            name: "Aguardando Humano",
            value: response.data.waitingHumanAttendance,
          },
          {
            name: "Humano",
            value: response.data.humanAttendance,
          },
        ];

        const totalClosed =
          response.data.closedByAI +
          response.data.closedByHumanAttendance +
          response.data.closedByTimeout;
        const totalInAttendance =
          response.data.aiAttendance +
          response.data.waitingHumanAttendance +
          response.data.humanAttendance;

        setStatusData(closedFormatted);
        setAttendanceData(attendanceFormatted);
        setStatusKpis({ totalClosed, totalInAttendance });
        setOpenProtocols(response.data.openProtocols ?? 0);
        setOperationWidgets(response.data.operationWidgets ?? null);
      } catch (err) {
        const message = getApiErrorMessage(err);
        setStatusError(message);
        error(message);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchStatusData();
  }, [
    startDate,
    endDate,
    timezone,
    granularity,
    error,
    refreshToken,
    canViewOperationWidgets,
  ]);

  const COLORS = [THEME.chart.primary, THEME.chart.success, THEME.chart.danger];

  function onPresetChange(value: PeriodSelectionPreset) {
    setPreset(value);
    if (value === "custom") return;

    const range = resolvePresetRange(value);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setGranularity(range.granularity);
    setYear(range.endDate.getFullYear());
    setMonth(range.endDate.getMonth() + 1);
  }

  function onReferenceYearChange(value: number) {
    const nextYear = clampYear(value);
    setYear(nextYear);
    setPreset("custom");
    setGranularity("month");

    const nextStart = new Date(nextYear, month - 1, 1);
    setStartDate(nextStart);
    setEndDate(endOfMonth(nextStart));
  }

  function onReferenceMonthChange(value: number) {
    const nextMonth = clampMonth(value);
    setMonth(nextMonth);
    setPreset("custom");
    setGranularity("month");

    const nextStart = new Date(year, nextMonth - 1, 1);
    setStartDate(nextStart);
    setEndDate(endOfMonth(nextStart));
  }

  function onStartDateChange(date: Date | null) {
    setPreset("custom");
    setGranularity("day");
    if (!date) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setStartDate(date);
    if (date) {
      setYear(date.getFullYear());
      setMonth(date.getMonth() + 1);
      if (endDate && endDate < date) {
        setEndDate(date);
      }
    }
  }

  function onEndDateChange(date: Date | null) {
    setPreset("custom");
    setGranularity("day");
    if (!date) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setEndDate(date);
    if (date) {
      setYear(date.getFullYear());
      setMonth(date.getMonth() + 1);
    }
  }

  function retryAnalytics() {
    setRefreshToken((previousValue) => previousValue + 1);
  }

  return (
    <div className="app-page mx-auto max-w-7xl">
      <BackButton />

      <div className="mb-6 mt-6 rounded p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <select
            className="form-input"
            value={preset}
            onChange={(e) =>
              onPresetChange(e.target.value as PeriodSelectionPreset)
            }
          >
            {PRESET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <DatePicker
            selected={startDate}
            onChange={onStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Data inicial"
            className="form-input"
            dateFormat="dd/MM/yyyy"
            isClearable
          />

          <DatePicker
            selected={endDate}
            onChange={onEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate || undefined}
            placeholderText="Data final"
            className="form-input"
            dateFormat="dd/MM/yyyy"
            isClearable
          />

          <select
            className="form-input"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {timezoneOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              className="form-input"
              min={1970}
              max={2100}
              value={year}
              onChange={(e) => onReferenceYearChange(Number(e.target.value))}
            />

            <select
              className="form-input"
              value={month}
              onChange={(e) => onReferenceMonthChange(Number(e.target.value))}
            >
              {months.map((monthLabel, index) => (
                <option key={monthLabel} value={index + 1}>
                  {monthLabel}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mb-8">
        {volumeError && (
          <InlineError message={volumeError} onRetry={retryAnalytics} />
        )}

        {isLoadingVolume ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <MetricCardSkeleton />
            {schedulingEnabled && (
              <>
                <MetricCardSkeleton />
                <MetricCardSkeleton />
                <MetricCardSkeleton />
              </>
            )}
            {protocolsEnabled && <MetricCardSkeleton />}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <DashboardKpiCard
              icon={UsersRound}
              tone="blue"
              label="Total de Atendimentos"
              value={integerFormatter.format(
                combinedVolumeKpis.totalConversations,
              )}
            />
            {schedulingEnabled && (
              <>
                <DashboardKpiCard
                  icon={CalendarCheck2}
                  tone="emerald"
                  label="Atendimentos com Agendamento"
                  value={integerFormatter.format(combinedVolumeKpis.scheduled)}
                />
                <DashboardKpiCard
                  icon={CircleX}
                  tone="rose"
                  label="Atendimentos sem Agendamento"
                  value={integerFormatter.format(
                    combinedVolumeKpis.unscheduled,
                  )}
                />
                <DashboardKpiCard
                  icon={ChartColumn}
                  tone="violet"
                  label="Conversão em Agendamento"
                  value={`${percentFormatter.format(combinedVolumeKpis.conversionRate)}%`}
                />
              </>
            )}
            {protocolsEnabled && (
              <DashboardKpiCard
                icon={ClipboardList}
                tone="amber"
                label="Protocolos abertos no período"
                value={integerFormatter.format(openProtocols)}
              />
            )}
          </div>
        )}
      </div>

      <div className="mb-4">
        <h2 className="mb-4 mt-10 text-2xl font-bold text-slate-900">
          Atendimentos por status
        </h2>

        {statusError && (
          <InlineError message={statusError} onRetry={retryAnalytics} />
        )}

        {isLoadingStatus ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <ChartSkeleton />
            <ChartSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-center">
                <span className="inline-flex items-center gap-1">
                  Atendimentos finalizados
                  <MetricHelp
                    helpText="Conversas encerradas pela IA, por um atendente humano ou automaticamente por falta de resposta."
                    label="Definições de métricas de atendimentos finalizados"
                  />
                </span>
              </h3>

              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {statusData.map((_, index) => (
                      <Cell
                        key={`cell-closed-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-center">
                <span className="inline-flex items-center gap-1">
                  Atendimentos em andamento
                  <MetricHelp
                    helpText="Conversas abertas em atendimento pela IA, aguardando um atendente ou em atendimento humano."
                    label="Definições de métricas de atendimentos em andamento"
                  />
                </span>
              </h3>

              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={attendanceData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {attendanceData.map((_, index) => (
                      <Cell
                        key={`cell-attendance-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {canViewOperationWidgets && (
        <div className="mt-8 space-y-6">
          <div className="app-card p-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Heatmap de volume (hora x dia)
            </h2>
            {isLoadingStatus && !operationWidgets ? (
              <div className="mt-4 h-80 animate-pulse rounded bg-slate-100" />
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-[900px] table-fixed border-collapse">
                  <thead>
                    <tr>
                      <th className="w-24 border border-slate-200 bg-slate-50 px-2 py-2 text-left text-xs text-slate-600">
                        Hora
                      </th>
                      {WEEKDAY_ORDER.map((weekday) => (
                        <th
                          key={weekday.value}
                          className="border border-slate-200 bg-slate-50 px-2 py-2 text-center text-xs text-slate-600"
                        >
                          {weekday.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {HOURS.map((hour) => {
                      const row = heatmapByHour.get(hour);
                      return (
                        <tr key={hour}>
                          <td className="border border-slate-200 px-2 py-2 text-xs font-medium text-slate-600">
                            {formatHour(hour)}
                          </td>
                          {WEEKDAY_ORDER.map((weekday) => {
                            const total = row ? row[weekday.value] : 0;
                            return (
                              <td
                                key={`${hour}-${weekday.value}`}
                                className={`border border-slate-200 px-2 py-2 text-center text-xs font-medium ${heatCellClass(total, maxHeat)}`}
                              >
                                {total}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="app-card p-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Ranking de produtividade
            </h2>
            {isLoadingStatus && !operationWidgets ? (
              <div className="mt-4 h-48 animate-pulse rounded bg-slate-100" />
            ) : operationWidgets?.productivityRanking.length ? (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-[860px] w-full table-auto divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Atendente
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Resolvidos
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        FRT mediana
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        MTTR mediana
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                        SLA breach
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {operationWidgets.productivityRanking.map((item) => (
                      <tr key={item.attendantId}>
                        <td className="px-3 py-2 text-sm font-medium text-slate-700">
                          {item.attendantName}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-700">
                          {item.resolved}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-700">
                          {formatMinutes(item.frtMedianMinutes)}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-700">
                          {formatMinutes(item.mttrMedianMinutes)}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-700">
                          {formatPercent(item.slaBreachRate)} (
                          {item.slaBreached}/{item.slaTotal})
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">
                Ainda não há resoluções suficientes para gerar ranking.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
